export type BaseModel = {
  id: string;
  created: string;
  updated: string;
};

export type PaginatedResponse<T> = {
  readonly next: string | null;
  readonly prev: string | null;
  readonly results: T[];
}

export enum EditMode {
  READONLY = 'READONLY',
  EDIT = 'EDIT',
}

export enum MfaMethodType {
  FIDO2 = 'fido2',
  TOTP = 'totp',
  BACKUP = 'backup',
}

export type MfaMethod = {
  readonly id: string;
  readonly method_type: MfaMethodType;
  name: string;
  is_primary: boolean;
};

export const mfaMethodChoices = Object.freeze([
  { value: MfaMethodType.FIDO2, text: 'Security Key (FIDO2)', icon: 'mdi-key' },
  { value: MfaMethodType.TOTP, text: 'Authenticator App (TOTP)', icon: 'mdi-cellphone-key' },
  { value: MfaMethodType.BACKUP, text: 'Backup Codes', icon: 'mdi-lock-reset' },
]);

export type ApiToken = BaseModel & {
  name: string;
  expire_date: string|null;
  token?: string;
};

export type User = BaseModel & {
  readonly last_login: string;
  readonly is_mfa_enabled: boolean;

  email: string;
  first_name: string | null;
  last_name: string | null;
  readonly name: string | null;
  is_active: boolean;
  is_superuser: boolean;
};

export type LockInfo = {
  created: string;
  updated: string;
  last_ping: string;
  expires: string;
  user: User;
};

export interface Lockable {
  lock_info: LockInfo|null,
}

export enum LoginResponseStatus {
  SUCCESS = 'success',
  MFA_REQUIRED = 'mfa-required',
}

export type LoginResponse = {
  status: LoginResponseStatus,
  first_login?: boolean,
  mfa?: MfaMethod[],
}

export enum RequestType {
  QUOTE = 'quote',
  BUY = 'buy',
};

export enum DeploymentType {
  SELF_HOSTED = 'self-hosted',
  CLOUD = 'cloud',
};

export enum TrialSignupType {
  COMPANY = 'company',
  ACADEMIA = 'academia',
  EVENT = 'event',
};

export enum PaymentMethod {
  CREDIT_CARD = 'credit-card',
  PAYPAL = 'paypal',
  WIRE_TRANSFER = 'wire-transfer',
};

export enum OrderStep {
  PRODUCT = 'product',
  BILLING_INFO = 'billing',
  INVOICE = 'invoice',
  PADDLE = 'paddle',
  SUCCESS = 'success',
  ERROR = 'error',
};

export type Customer = {
  name: string;
  vat_number: string;
  first_line: string;
  city: string;
  country: string|null;
  postal_code: string;
};

export type Currency = 'USD' | 'EUR' | 'GBP' | 'CHF' | 'SEK' | 'NOK' | 'DKK' | 'AUD';

export type OrderSignupForm = {
  id: string;
  po_number: string;
  customer: Customer;
  currency: Currency;
  order_email: string;
  request_type: RequestType;
  deployment_type: DeploymentType;
  payment_method: PaymentMethod;
  subdomain: string;
  confirmed_terms: boolean;
  user_quantity: number;
  paddle: boolean;
};

export type TrialSignupForm = {
  id: string;
  type: TrialSignupType;
  email: string;
  deployment_type: DeploymentType;
  confirmed_terms: boolean;
  country: string|null;
  organization_name: string;
  users_count: number | null;
  end_date: string | null;
};

type CurrencyPriceID = {
  EUR: string;
  USD: string;
  GBP: string;
  CHF: string;
  SEK: string;
  NOK: string;
  DKK: string;
  AUD: string;
};

export type PriceIDs = {
  test: {
    self_hosted: CurrencyPriceID;
    cloud: CurrencyPriceID;
  };
  prod: {
    self_hosted: CurrencyPriceID;
    cloud: CurrencyPriceID;
  };
};

export type ManagedInstanceStatus = {
  license_data: object;
  db_size_bytes:number;
  last_update: string;

};

export type ManagedInstance = {
  id: string;
  customer_name: string;
  customer_nonce: string;
  ENABLED_PLUGINS: string;
  INSTANCE_TAGS: string;
  ENABLE_APM: boolean;
  ENABLE_PRIVATE_DESIGNS: boolean;
  LICENSE: string;
  SPELLCHECK_DICTIONARY_PER_USER: boolean;
  SPELLCHECK_MODE_PICKY: boolean;
  ingress_name: string;
  version_branch: string;
  stale: boolean;
  status: ManagedInstanceStatus;
  marked_for_delete: boolean;
  warnings: string[];
  errors: string[];

  s3_files_size?: number;
  backup_files_size?: number;
  backup_count_daily?: number;
  backup_count_weekly?: number;
  backup_count_monthly?: number;
  backup_latest?: string;
}
